import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import GalleryGrid from '../components/GalleryGrid';

export default function ProblemSolving() {
  const galleryImages = [
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/problemlosning-optimering.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/automatiske-maskiner-montage.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-f13a7513b1d94a9ca64d009f1172fcf7-mv2.jpg',
  ];

  return (
    <div className="bg-[#0b1017] text-slate-100 min-h-screen">
      <SEO
        title="Problemløsninger - JARCon ApS"
        description="Tekniske udfordringer, procesoptimering, fejlretning på maskiner og automatisering af manuelle opgaver. Vedholdende problemløsning."
      />

      <PageHeader
        badge="Fejlfinding & Optimering"
        title="Problemløsninger"
        subtitle="Tekniske udfordringer • Optimering • Specialløsninger • Automatisering af manuelle opgaver"
        breadcrumbs={[
          { label: 'Automation', path: '/automation' },
          { label: 'Problemløsninger' },
        ]}
        ctaText="Beskriv dit problem for os"
        ctaLink="/kontakt"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 space-y-8">
            <div className="p-8 rounded-xl bg-[#0e1622] border border-slate-800 space-y-4">
              <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest font-semibold block">
                Vedholdende problemløsning
              </span>
              <h2 className="font-display text-2xl font-bold text-white">
                Når produktionen driller eller maskinen svigter
              </h2>
              <blockquote className="border-l-2 border-cyan-400 pl-4 italic text-slate-200 text-base">
                &ldquo;Jeg er meget vedholdende af natur og slipper ikke en opgave, inden den er løst til alles tilfredshed.&rdquo;
              </blockquote>
              <p className="text-slate-400 text-sm leading-relaxed">
                I en travl produktionshverdag kan mekaniske fejl, gentagne stop, unøjagtigheder eller uhensigtsmæssige manuelle arbejdsgange koste store summer. Med en solid baggrund fra maskinbranchen og mange år som vedligeholder kan JARCon hurtigt lokalisere problemets rod og implementere en holdbar mekanisk eller elektronisk løsning.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-[#0e1622] border border-slate-800 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-950/70 border border-cyan-500/30 text-cyan-400 flex items-center justify-center text-lg">
                  <i className="fa-solid fa-magnifying-glass-chart" aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-lg text-white">Fejlretning &amp; Driftstab</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Systematisk fejlfinding på mekaniske samlinger, lejer, sensorik og PLC-logik, der forårsager utilsigtede stop.
                </p>
              </div>

              <div className="p-6 rounded-xl bg-[#0e1622] border border-slate-800 space-y-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-950/70 border border-cyan-500/30 text-cyan-400 flex items-center justify-center text-lg">
                  <i className="fa-solid fa-gauge-high" aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-lg text-white">Automatisering af manuelle opgaver</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Konstruktion af specialudstyr eller robotløsninger, der erstatter ensidigt gentaget arbejde og fjerner flaskehalse.
                </p>
              </div>
            </div>

            <GalleryGrid
              title="Problemløsning og procesoptimering"
              subtitle="Klik på billederne for at åbne i fuld skærm"
              images={galleryImages}
            />
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="p-6 rounded-xl bg-[#0e1622] border border-slate-800">
              <h3 className="font-display text-base font-bold text-white mb-4">
                Fokusområder
              </h3>
              <ul className="space-y-3 text-xs font-open-sans text-slate-300">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-cyan-400" aria-hidden="true" />
                  <span>Tekniske udfordringer</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-cyan-400" aria-hidden="true" />
                  <span>Optimering af maskinhastighed</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-cyan-400" aria-hidden="true" />
                  <span>Specialløsninger og ombygning</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-cyan-400" aria-hidden="true" />
                  <span>Fejl eller ineffektive processer</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-cyan-400" aria-hidden="true" />
                  <span>Automatisering af manuelle opgaver</span>
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-gradient-to-br from-[#0e1622] to-[#070b10] border border-cyan-500/40 text-center space-y-4">
              <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-wider block font-semibold">
                Står produktionen stille?
              </span>
              <p className="text-xs text-slate-300">
                Ring direkte til Jens Christensen på +45 22 88 45 36 for hurtig teknisk bistand.
              </p>
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-xs uppercase tracking-wider transition-all"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
